import { formatBulkResultLines } from './userProfileFields';

export const JOB_STATES = ['queued', 'running', 'done', 'failed'];
const FINAL_STATES = ['done', 'failed'];

/** The upload is queued when the API answers with a job id instead of row results. */
export const bulkUploadJobId = response => response?.data?.job_id || response?.job_id || null;

const asRows = (rows, mapper) => (Array.isArray(rows) ? rows : []).map(mapper);

const person = row => (typeof row === 'string'
    ? { username: row, email: '' }
    : {
        row: row?.row ?? null,
        username: row?.username || row?.Username || '',
        email: row?.email || row?.Email || '',
        action: row?.action || row?.Action || '',
    });

/** One report shape for both the async job status and the legacy synchronous response. */
export const normaliseBulkJob = (data = {}) => {
    const state = JOB_STATES.includes(data.state) ? data.state : 'running';
    const created = asRows(data.created, person);
    const updated = asRows(data.updated, person);
    const moved = asRows(data.moved, person);
    const failed = asRows(data.failed, row => ({ ...person(row), reason: row?.reason || row?.Reason || '' }));
    const total = Number(data.total) || 0;
    const processed = Number.isFinite(Number(data.processed))
        ? Number(data.processed)
        : created.length + updated.length + moved.length + failed.length;
    return { state, total, processed, created, updated, moved, failed, message: data.message || '' };
};

export const legacyBulkResult = (response) => {
    const lines = formatBulkResultLines(response?.data);
    const created = lines.processed.map(row => ({ username: row.username, email: '', action: row.action }));
    const failed = lines.failed.map(row => ({ username: row.username, email: '', reason: row.reason }));
    return {
        state: 'done',
        total: created.length + failed.length,
        processed: created.length + failed.length,
        created,
        updated: [],
        moved: [],
        failed,
        message: response?.message || '',
    };
};

export const isFinalJob = job => FINAL_STATES.includes(job?.state);

export const jobProgress = (job) => {
    if (!job?.total) return null;
    return Math.min(100, Math.round((job.processed / job.total) * 100));
};

const csvCell = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildFailureCsv = failed => [
    ['Row', 'Email', 'Username', 'Reason'],
    ...failed.map(row => [row.row ?? '', row.email, row.username, row.reason]),
].map(cells => cells.map(csvCell).join(',')).join('\r\n');

export const downloadTextFile = (filename, text, type = 'text/csv;charset=utf-8') => {
    const blob = new Blob([`\uFEFF${text}`], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Polls the job until it finishes. `onProgress` receives every status.
 * Returns the final normalised job, or a failed job when polling times out or is cancelled.
 */
export const pollBulkUploadJob = async (jobId, fetchStatus, {
    onProgress,
    intervalMs = 3000,
    timeoutMs = 30 * 60 * 1000,
    isCancelled = () => false,
} = {}) => {
    const startedAt = Date.now();
    let failures = 0;
    while (!isCancelled()) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const response = await fetchStatus(jobId);
            if (response?.status === false) throw new Error(response.message || 'Upload status could not be read.');
            const job = normaliseBulkJob(response?.data || response);
            failures = 0;
            if (onProgress) onProgress(job);
            if (isFinalJob(job)) return job;
        } catch (error) {
            failures += 1;
            if (failures >= 5) {
                return { ...normaliseBulkJob({ state: 'failed' }), message: error?.message || 'Upload status could not be read.' };
            }
        }
        if (Date.now() - startedAt > timeoutMs) {
            return { ...normaliseBulkJob({ state: 'failed' }), message: 'The upload is taking longer than expected. Check the user list later.' };
        }
        // eslint-disable-next-line no-await-in-loop
        await wait(intervalMs);
    }
    return { ...normaliseBulkJob({ state: 'failed' }), message: 'Upload tracking stopped.' };
};
